import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Search } from "lucide-react";
import { PageHeader, Card, SectionTitle } from "@/components/common/PageHeader";
import { Loading } from "@/pages/_shared";
import api from "@/lib/api";

export default function Customers() {
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => {
      api.get(`/admin/customers${search.trim() ? `?search=${encodeURIComponent(search.trim())}` : ""}`)
        .then(({ data }) => setRows(data.customers)).catch(() => setRows([]));
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  if (!rows) return <Loading />;
  const customers = rows.filter((r) => r.role !== "admin");
  const admins = rows.filter((r) => r.role === "admin");

  return (
    <div className="space-y-6 animate-fade-up" data-testid="owner-customers-page">
      <PageHeader eyebrow="Platform" title="Customers & CRM"
        subtitle="Every account on the platform. Click a row to open the full CRM record — profile, credit, payments, approvals, notes and tasks." />

      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="p-4"><div className="font-mono text-[10px] uppercase text-slate-400">Total Accounts</div><div className="mt-1 font-head text-2xl font-extrabold text-slate-900">{rows.length}</div></Card>
        <Card className="p-4"><div className="font-mono text-[10px] uppercase text-slate-400">Customers</div><div className="mt-1 font-head text-2xl font-extrabold text-slate-900">{customers.length}</div></Card>
        <Card className="p-4"><div className="font-mono text-[10px] uppercase text-slate-400">Platform Admins</div><div className="mt-1 font-head text-2xl font-extrabold text-[#b0862f]">{admins.length}</div></Card>
      </div>

      <Card className="p-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <SectionTitle icon={Users}>All Accounts</SectionTitle>
          <div className="relative w-full max-w-xs">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="customer-search"
              placeholder="Search name, email or company…" className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-[13px] outline-none focus:border-[#1a5fd0]" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13px]" data-testid="customers-table">
            <thead>
              <tr className="border-b border-slate-100 font-mono text-[10px] uppercase tracking-wider text-slate-400">
                <th className="py-2 pr-4">Business</th><th className="py-2 pr-4">Contact</th><th className="py-2 pr-4">Role</th><th className="py-2 pr-4">Onboarding</th><th className="py-2 pr-4">Sign-in</th><th className="py-2">Joined</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-400">No accounts match your search.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} onClick={() => navigate(`/owner/customers/${r.id}`)} className="cursor-pointer border-b border-slate-50 hover:bg-slate-50" data-testid="customer-row">
                  <td className="py-2.5 pr-4 font-semibold text-slate-800">{r.company_name || "—"}</td>
                  <td className="py-2.5 pr-4 text-slate-600">{r.name || "—"}<div className="text-[11px] text-slate-400">{r.email}</div></td>
                  <td className="py-2.5 pr-4">
                    <span className={`rounded-full px-2 py-0.5 font-mono text-[9px] font-bold uppercase ${r.role === "admin" ? "bg-[#b0862f]/10 text-[#b0862f]" : "bg-slate-100 text-slate-500"}`}>{r.role}</span>
                  </td>
                  <td className="py-2.5 pr-4">{r.onboarding_complete ? <span className="text-emerald-600">Complete</span> : <span className="text-slate-400">In progress</span>}</td>
                  <td className="py-2.5 pr-4 text-slate-500">{r.auth_provider === "google" ? "Google" : "Email"}</td>
                  <td className="py-2.5 text-slate-400">{String(r.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
